import { Radio, RadioGroup, Spacing, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { tariffUpsale } from 'reactApp/appHelpers/featuresHelpers/features/upsaleSmallTariffs';
import { BaseConfirmDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { getPeriodName } from 'reactApp/utils/Period';
import { formatPrice, getMonthProductPrice } from 'reactApp/utils/priceHelpers';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import styles from './UpsaleDialog.css';

type UpsaleDialogProps = {
    onClose?: () => void;
    selectedProduct: Product;
    activePeriod: 'm' | 'y';
    resolve: (product: Product) => void;
};

export const UpsaleDialog = memo<UpsaleDialogProps>(({ onClose, selectedProduct, activePeriod, resolve }) => {
    const monthProductUpTariff = useSelector((state: RootState) => ProductsSelectors.getProductById(state, tariffUpsale?.month));
    const yearProductUpTariff = useSelector((state: RootState) => ProductsSelectors.getProductById(state, tariffUpsale?.year));
    const monthProduct = activePeriod === 'm' ? selectedProduct : undefined;
    const yearProduct = activePeriod === 'y' ? selectedProduct : undefined;
    const selectedTariffId = selectedProduct.id;
    const valueSmallTariff = selectedProduct.space.value;
    const startActiveChoosenSpace = monthProductUpTariff?.space.space;
    const startActiveChoosen = monthProductUpTariff?.id;
    const valueUpTariff = monthProductUpTariff?.space.value;
    const monthUpTariffPrice = useMemo(() => getMonthProductPrice(monthProductUpTariff), [monthProductUpTariff]);
    const yearUpTariffPrice = useMemo(() => getMonthProductPrice(yearProductUpTariff), [yearProductUpTariff]);
    const trialPeriod = getPeriodName(monthProductUpTariff?.trialPeriod || '');

    const monthProductPrice = useMemo(() => {
        return monthProduct ? getMonthProductPrice(monthProduct) : undefined;
    }, [monthProduct]);

    const yearMonthProductPrice = useMemo(() => {
        return yearProduct ? getMonthProductPrice(yearProduct) : undefined;
    }, [yearProduct]);

    const [chosenTariff, setChosenTariff] = useState(startActiveChoosen);

    const sendAnalitics = (label: string, tariff?: string) => {
        sendQuotaGa({
            action: 'modal-choose-tariff',
            label,
            tariff,
        });
    };

    useEffect(() => {
        sendAnalitics('show', chosenTariff);
        emitAnalyticEvent(AnalyticEventNames.UPSALE_BAZ_MODAL_SHOW);
    }, []);

    const switchTariff = useCallback(
        (tariff) => {
            sendAnalitics('switch', tariff);
            emitAnalyticEvent(AnalyticEventNames.UPSALE_BAZ_MODAL_SWITCH);
            setChosenTariff(tariff);
        },
        [setChosenTariff]
    );

    const handleClose = useCallback(() => {
        sendAnalitics('close');
        emitAnalyticEvent(AnalyticEventNames.UPSALE_BAZ_MODAL_CLOSE);
        onClose?.();
    }, [onClose]);

    const handleSuccess = useCallback(() => {
        sendAnalitics('continue', chosenTariff);
        emitAnalyticEvent(AnalyticEventNames.UPSALE_BAZ_MODAL_CONTINUE);
        onClose?.();

        if (chosenTariff === startActiveChoosen) {
            if (activePeriod === 'm' && monthProductUpTariff) {
                resolve(monthProductUpTariff);
            } else if (yearProductUpTariff) {
                resolve(yearProductUpTariff);
            }
        } else if (activePeriod === 'm' && monthProduct) {
            resolve(monthProduct);
        } else if (yearProduct) {
            resolve(yearProduct);
        }
    }, [chosenTariff, activePeriod, monthProductUpTariff, yearProductUpTariff, monthProduct, yearProduct, resolve]);

    const renderHeader = useCallback(
        () => (
            <div className={styles.textBlock}>
                <Title className={styles.headerTitle}>C&nbsp;тарифом {startActiveChoosenSpace}&nbsp;ГБ у&nbsp;вас будет безлимит</Title>
                <Spacing size={4} />
                <Text className={styles.headerText}>
                    Цена чуть выше, зато с&nbsp;безлимитом фото с&nbsp;телефона не&nbsp;тратят память Облака
                </Text>
            </div>
        ),
        [startActiveChoosenSpace]
    );

    const renderContent = useCallback(() => {
        return (
            <RadioGroup className={styles.radioGroup}>
                <Radio
                    className={classNames(styles.radio, {
                        [styles['radio_active']]: chosenTariff === startActiveChoosen,
                    })}
                    name="activeTariff"
                    value="tariffBAZ"
                    description={
                        <Text className={styles.radioDescription}>
                            {activePeriod === 'm' ? (
                                <>
                                    {trialPeriod} за&nbsp;0&nbsp;₽, далее {formatPrice(monthUpTariffPrice)}&nbsp;₽ в&nbsp;месяц
                                </>
                            ) : (
                                <>{formatPrice(yearUpTariffPrice)}&nbsp;₽ в&nbsp;месяц при покупке на&nbsp;год</>
                            )}
                        </Text>
                    }
                    checked={chosenTariff === startActiveChoosen}
                    onClick={() => switchTariff(startActiveChoosen)}
                >
                    <Title level="2" className={styles.radioTitle}>
                        +{valueUpTariff}
                    </Title>
                    <div className={styles.label}>Безлимит</div>
                </Radio>

                <Spacing size={8} />

                <Radio
                    className={classNames(styles.radio, {
                        [styles['radio_active']]: chosenTariff === selectedTariffId,
                    })}
                    name="activeTariff"
                    value="smallTariff"
                    description={
                        <Text className={styles.radioDescription}>
                            {activePeriod === 'm' ? (
                                <>{formatPrice(monthProductPrice)}&nbsp;₽ в&nbsp;месяц</>
                            ) : (
                                <>{formatPrice(yearMonthProductPrice)}&nbsp;₽ в&nbsp;месяц при покупке на&nbsp;год</>
                            )}
                        </Text>
                    }
                    checked={chosenTariff === selectedTariffId}
                    onClick={() => switchTariff(selectedTariffId)}
                >
                    <Title className={styles.radioTitle} level="2">
                        +{valueSmallTariff}
                    </Title>
                </Radio>
            </RadioGroup>
        );
    }, [chosenTariff, monthProductPrice, yearMonthProductPrice, switchTariff, monthProduct, yearProduct, activePeriod]);

    const containsDarkTheme = isDarkThemeModeEnabled();

    return (
        <BaseConfirmDialog
            renderHeader={renderHeader}
            size="average"
            successButtonText="Продолжить"
            buttons={[EButtonType.accept]}
            onClose={handleClose}
            onSuccess={handleSuccess}
            renderContent={renderContent}
            closeOnDimmerClick
            isDarkTheme={containsDarkTheme}
            buttonTheme="vk"
            className={styles.root}
        />
    );
});

UpsaleDialog.displayName = 'UpsaleDialog';
